import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import RequestAccessLink from 'auth/RequestAccessLink';
import {FolderSection, FolderSummary, StringIDFolder} from 'constants/FolderConstants';
import {RegistrySummary} from 'constants/RegistryConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import normalizeFolder from 'folders/util/normalizeFolder';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CrumbLink, SectionCrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {
  FolderListingCrumbsQuery,
  FolderListingCrumbsQueryVariables,
} from 'nav/sidepanel/listing/__generated/FolderListingCrumbsQuery';
import {getDefaultFolderSearchParams} from 'nav/sidepanel/listing/FolderSearchParams';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, Operator, SearchParams} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import EntityStore from 'stores/EntityStore';
import Popover from 'ui-library/floating-ui/Popover';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import FolderSettingsIcon from './FolderSettingsIcon';

type CrumbFolderGQL = ExtractByTypename<FolderListingCrumbsQuery['folder'], 'FolderNode'>;

type CrumbParentFolderGQL = CrumbFolderGQL['parentFolders'][number];

interface BaseProps {
  onChangeSection?: (section: FolderSection) => void;
  onOpenFolder?: (folder: StringIDFolder, searchParams: SearchParams) => void;
  onNavigateToRoot: () => void;
  searchParams: SearchParams;
  sectionName: string;
  section?: FolderSection;
  showSectionDropdown?: boolean;
  activeSavedSearch?: SavedSearch;
  settingsComponents?: JSX.Element;
}

interface PresentionalFolderListingCrumbsProps extends BaseProps {
  ancestralFolders: Array<AncestralFolder>;
}

interface QueryingFolderListingCrumbsProps extends BaseProps {
  folderId: string;
}

interface FolderCrumbLinkProps {
  folder: StringIDFolder;
  isDisabled: boolean;
  searchParams: SearchParams;
  section?: FolderSection;
  showSectionDropdown?: boolean;
  onChangeSection?: (section: FolderSection) => void;
  onOpenFolder?: (folder: StringIDFolder, searchParams: SearchParams) => void;
  readable: boolean;
}

// This function cannot be a component because Breadcrumbs expects the components it receives
// (SectionCrumbLink and CrumbLink) to have certain props (e.g. name, icon, etc.)
export function getFolderCrumbLink(props: FolderCrumbLinkProps): JSX.Element {
  function handleClick(): void {
    if (!props.isDisabled && props.onOpenFolder) {
      props.onOpenFolder(
        props.folder,
        SearchUtil.mergeFiltersToSearchParams(getDefaultFolderSearchParams(props.folder), [
          {
            key: FilterKey.FOLDER,
            operator: Operator.IS,
            value: props.folder.id,
          },
        ])
      );
    }
  }

  const sharedProps = {
    icon:
      !props.readable && !props.folder.source?.alwaysShowAncestorFolderNames ? (
        <BenchlingIcon icon="lock" />
      ) : undefined,
    text: RegistryUtil.isRegistryFolder(props.folder)
      ? FileBrowserUtil.getRegistryName(props.folder.source as RegistrySummary)
      : props.folder.name,
  };
  if (props.showSectionDropdown) {
    assert(
      Boolean(props.section && props.onChangeSection),
      'section and onChangeSection must be defined if showSectionDropdown is enabled'
    );
    return (
      <SectionCrumbLink
        {...sharedProps}
        onClick={handleClick}
        key={props.folder.id}
        section={props.section!}
        onChangeSection={props.onChangeSection!}
      />
    );
  }
  return <CrumbLink {...sharedProps} onClick={props.isDisabled ? null : handleClick} key={props.folder.id} />;
}

function maybeAddRequestAccessPopover(
  crumb: JSX.Element,
  folderId: string,
  isSignedIn: boolean,
  readable: boolean
): JSX.Element {
  if (isSignedIn && !readable) {
    return (
      <Popover
        key={folderId}
        renderContent={() => (
          <div className="u-text-align-center u-base-text">
            <p>You do not have access to open this folder.</p>
            <RequestAccessLink resourceId={folderId} />
          </div>
        )}
      >
        {crumb}
      </Popover>
    );
  }
  return crumb;
}

export function PresentationalFolderListingCrumbs(props: PresentionalFolderListingCrumbsProps): JSX.Element {
  const crumbs: Array<JSX.Element> = [];
  const isSignedIn = EntityStore.currentUser() != null;
  crumbs.push(
    <CrumbLink key="root" text={props.sectionName} onClick={isSignedIn ? props.onNavigateToRoot : null} />
  );
  const firstInaccessibleAncestorIndex = props.ancestralFolders.findIndex(
    (folder) => !folder.permissions.readable
  );
  const currentFolder = props.ancestralFolders[0];
  const crumbsPath =
    !currentFolder.source?.alwaysShowAncestorFolderNames && firstInaccessibleAncestorIndex > -1
      ? props.ancestralFolders.slice(0, firstInaccessibleAncestorIndex + 1).reverse()
      : props.ancestralFolders.slice().reverse();
  for (let i = 0; i < crumbsPath.length; i++) {
    const folder = crumbsPath[i];
    const readable = folder.permissions.readable;
    const crumb = getFolderCrumbLink({
      folder,
      searchParams: props.searchParams,
      isDisabled: !readable || (!props.activeSavedSearch && i === crumbsPath.length - 1),
      showSectionDropdown: props.showSectionDropdown && i === 0,
      section: props.section,
      onChangeSection: props.onChangeSection,
      onOpenFolder: props.onOpenFolder,
      readable,
    });
    crumbs.push(maybeAddRequestAccessPopover(crumb, folder.id, isSignedIn, readable));
  }
  if (props.activeSavedSearch) {
    crumbs.push(<CrumbLink key="savedSearch" text={props.activeSavedSearch.name} />);
  }
  let settingsIcon;
  if (isSignedIn) {
    settingsIcon = (
      <>
        {currentFolder ? (
          <FolderSettingsIcon folder={currentFolder} onNavigateToRoot={props.onNavigateToRoot} />
        ) : undefined}
        {props.settingsComponents}
      </>
    );
  }

  return <SplitBreadcrumbs crumbs={crumbs} settingsIcon={settingsIcon} />;
}

/** @package */
export type AncestralFolder = StringIDFolder & {permissions: {readable: boolean}};

function graphqlFolderToAncestralFolders(folder: CrumbFolderGQL): Array<AncestralFolder> {
  const ancestralFolders: Array<AncestralFolder> = [];

  const folderIdToReadable = folder.parentFolders.reduce(
    (map: Map<string, boolean>, ancestorFolder: CrumbParentFolderGQL) => {
      map.set(ancestorFolder.id, ancestorFolder.permissions.readable);
      return map;
    },
    new Map<string, boolean>()
  );
  folderIdToReadable.set(folder.id, folder.permissions.readable);

  let restFolder: FolderSummary | null = graphQLToREST('FolderWithPermissions', folder);
  while (restFolder != null) {
    const normalizedFolder = normalizeFolder(restFolder);
    ancestralFolders.push({
      ...normalizedFolder,
      permissions: {
        ...normalizedFolder.permissions!,
        readable: folderIdToReadable.get(normalizedFolder.id) ?? false,
      },
    });
    restFolder = restFolder.parent;
  }

  return ancestralFolders;
}

export const FOLDER_LISTING_CRUMBS_QUERY: DocumentNode = gql`
  query FolderListingCrumbsQuery($id: StrictString!) @skipAudit @engComponent(name: CORE_OBJECT) {
    folder(id: $id) {
      ... on FolderNode {
        ...FolderWithPermissionsGraphQL
        parentFolders {
          id
          permissions {
            readable
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('FolderWithPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingFolderListingCrumbs(
  props: QueryingFolderListingCrumbsProps
): JSX.Element | null {
  return (
    <BenchlingQuery<FolderListingCrumbsQuery, FolderListingCrumbsQueryVariables>
      query={FOLDER_LISTING_CRUMBS_QUERY}
      variables={{id: props.folderId}}
      renderLoading={() => <HorizontalThrobber />}
    >
      {(data) => {
        if (isInaccessible(data.folder)) {
          // We expect data.folder to be accessible, but it may be inaccessible when the user is logged out
          return null;
        }
        const ancestralFolders = graphqlFolderToAncestralFolders(data.folder);
        return <PresentationalFolderListingCrumbs {...props} ancestralFolders={ancestralFolders} />;
      }}
    </BenchlingQuery>
  );
}
